/**
 * Inline reactions pubsub handlers, mirroring the inline-comments pubsub flow
 * (`@confluence/comments-pubsub/src/inline/rendererInlineCommentsPubSub.ts`).
 *
 * Three new AGG fields ride on the existing `confluence_onContentModified`
 * subscription:
 *   - `inlineReactionAdded`              → User added a reaction (creates a
 *     new highlight when `newHighlight=true`, otherwise increments an
 *     existing reaction count).
 *   - `inlineReactionRemoved`            → A reaction was removed but the
 *     highlight still has other reactions remaining.
 *   - `inlineReactionHighlightDeleted`   → The last reaction was removed,
 *     causing the anchor comment to be deleted and the ADF annotation mark
 *     to be removed.
 *
 */

import { AnnotationTypes } from '@atlaskit/adf-schema';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { RendererActions } from '@atlaskit/renderer/actions';

import type {
	ConfluenceOnContentModified,
	SubscriptionsDispatchType,
} from '@confluence/graphql/entry-points/subscriptionTypes.ts';
import { expVal } from '@confluence/feature-experiments';
import { extractNumericCommentId } from '../utils/extractNumericCommentId.ts';

export type InlineReactionsContentType = 'page' | 'blogpost';

export type ShowReactionFlag = (event: {
	commentId: string;
	markerRef: string;
	emojiId?: string;
	contentType: InlineReactionsContentType;
	/**
	 * AAID of the user that produced the reaction. Receivers can use this to
	 * resolve the actor's display name via `confluence_reactedUsers` for the
	 * "{name} reacted to this page" flag copy.
	 */
	actorAccountId?: string;
}) => void;

export type RefetchReactionsForComment = (commentId: string) => void;

export type InlineReactionsPubSubParams = {
	contentId: string;
	contentType?: InlineReactionsContentType;
	currentUserId: string | null;
	rendererActions?: RendererActions;
	updateDocument?: (newAdf: JSONDocNode) => void;
	addOptimisticReaction: (markerRef: string, commentId: string) => void;
	/**
	 * Drops local optimistic markers for the given markerRef, used when the
	 * highlight is deleted by another user.
	 */
	pruneOptimisticReactions: (confirmedMarkerRefs: readonly string[]) => void;
	/**
	 * Triggers a refetch of `confluence_contentReactionsSummary` for the
	 * affected commentId so the receiver's `count` and `reacted` fields are
	 * authoritative (the pubsub `reacted` field reflects the originator's
	 * state, not the receiver's).
	 */
	refetchReactionsSummary: RefetchReactionsForComment;
	/**
	 * Triggers a refetch of `confluence_activeInlineData` so the new
	 * markerRef → commentId mapping is propagated to viewers.
	 */
	refetchActiveInlineData: () => void;
	/**
	 * Marks the receiver's loaded document as out-of-date so the existing
	 * page-content refetch flow re-pulls the published ADF. Used as the
	 * Option-A interim path for `inlineReactionAdded {newHighlight: true}`
	 * until the BE adds `step` + `publishVersionNumber` to the event payload
	 * (see TODO in `onInlineReactionAdded`).
	 */
	markDocumentNeedsUpdate: () => void;
	/**
	 * Pushes a "{somebody} reacted to this page" flag through the FlagsStateContainer.
	 */
	showReactionFlag: ShowReactionFlag;
	/**
	 * Applies a remote reaction delta (+1 or -1) directly to the store for an
	 * instant UI update before the background refetch completes.
	 *
	 * Called with the numeric commentId (not ARI), the emojiId, and the delta
	 * direction. Only invoked when the event payload includes `emojiId`.
	 */
	applyRemoteDelta: (commentId: string, emojiId: string, delta: 1 | -1) => void;
	/**
	 * Optional: clears the active highlight if it matches the markerRef of a
	 * deleted highlight, so the renderer doesn't keep stale focus state.
	 */
	setActiveHighlight?: (markerRef: string | null) => void;
	/**
	 * Optional: writes a fetched comment into the comments panel's Apollo/Relay
	 * cache so newly added reactions appear in the panel without a page refresh.
	 * Matches the pattern used by the inline comments pubsub handler.
	 */
	addCommentInCache?: ({
		newComment,
		onError,
	}: {
		newComment: any;
		onError?: (error: Error) => void;
	}) => void;
	/**
	 * Optional: fetches a comment by ID and writes it into the shared cache.
	 * Used together with addCommentInCache to update the comments panel.
	 */
	fetchCommentById?: (params: {
		commentId?: string;
		markerRef?: string;
		contentId: string;
		fetchPolicy?: 'cache-first' | 'network-only' | 'cache-only' | 'no-cache';
	}) => Promise<any>;
	/**
	 * Optional: marks the reaction comment as unread so the content wrapper
	 * shows the blue-dot indicator — matching inline comment pubsub behaviour.
	 */
	updateUnreadCommentIDSet?: (
		unreadCommentIDSet: Set<string>,
		operation?: 'add' | 'remove' | 'removeAll' | 'initialize',
	) => void;
	/** Optional: reads the full comments query from the Apollo cache. */
	getCommentsDataFromCache?: (contentId: string) => any;
	/** Optional: returns actions for the processed comments data store. */
	getProcessedCommentsDataActions?: () => { processCommentsData: (params: any) => void };
	/** Optional: triggers a refresh of the legacy comments panel store. */
	triggerCommentPanelRefresh?: () => void;
	/** Optional: dispatch from `useSubscriptionsDispatchContext`. */
	addSubscriber?: SubscriptionsDispatchType['addSubscriber'];
	setContentId?: SubscriptionsDispatchType['setContentId'];
	setContentType?: SubscriptionsDispatchType['setContentType'];
};

export type InlineReactionsPubSubUnsubParams = {
	subscriptions: Array<{ subscriberId: string; delta: string }>;
	removeSubscriber?: SubscriptionsDispatchType['removeSubscriber'];
};

/** AGG subscription deltas for the three new inline-reaction events. */
export const InlineReactionSubscriptionDeltas = {
	InlineReactionAdded: 'inlineReactionAdded',
	InlineReactionRemoved: 'inlineReactionRemoved',
	InlineReactionHighlightDeleted: 'inlineReactionHighlightDeleted',
} as const;

export type InlineReactionDelta =
	(typeof InlineReactionSubscriptionDeltas)[keyof typeof InlineReactionSubscriptionDeltas];

/** Internal: returns true when the event was authored by the current user. */
const isSelfAuthored = (
	payload: ConfluenceOnContentModified | undefined,
	currentUserId: string | null,
) => Boolean(payload?.accountId && currentUserId && payload.accountId === currentUserId);

/**
 * Build a fresh `inlineReactionAdded` subscriber bound to the given params.
 *
 * We construct subscribers via factories (rather than module-scoped functions
 * sharing a mutable `params` slot) so each `setUpInlineReactionsPubSub`
 * invocation gets its own closure with no cross-mount state. This also means
 * tests don't need a module-level reset hook.
 */
export const createInlineReactionAddedHandler =
	(params: InlineReactionsPubSubParams) =>
	(_event: string, payload: ConfluenceOnContentModified): void => {
		if (isSelfAuthored(payload, params.currentUserId)) {
			return;
		}
		const event = payload?.inlineReactionAdded;
		if (!event || !event.commentId || !event.markerRef) {
			return;
		}

		const {
			addOptimisticReaction,
			applyRemoteDelta,
			refetchReactionsSummary,
			refetchActiveInlineData,
			markDocumentNeedsUpdate,
			showReactionFlag,
			contentType,
			contentId,
			addCommentInCache,
			fetchCommentById,
			updateUnreadCommentIDSet,
			getCommentsDataFromCache,
			getProcessedCommentsDataActions,
			triggerCommentPanelRefresh,
		} = params;

		if (event.newHighlight) {
			// Seed the optimistic store + refresh `activeInlineData` so the
			// receiver classifies the new markerRef as a reaction, and
			// trigger a content refetch so the markerRef actually lands in
			// the loaded ADF.
			// When BE adds `step` + `publishVersionNumber` to `inlineReactionAdded`, replace
			// `markDocumentNeedsUpdate()` with `rendererActions.applyAnnotation({from, to}, ...)`
			// for an instant paint with no content refetch.
			addOptimisticReaction(event.markerRef, event.commentId);
			refetchActiveInlineData();
			markDocumentNeedsUpdate();
		}

		// Apply the delta directly to the store for an instant count update.
		// Only when emojiId is present — without it we can't identify which
		// emoji to increment. The background refetch will always correct the
		// authoritative state regardless.
		if (event.emojiId) {
			applyRemoteDelta(extractNumericCommentId(event.commentId), event.emojiId, 1);
		}

		// Refetch for authoritative counts + `reacted` state for the current user.
		refetchReactionsSummary(event.commentId);

		// Update the comments panel cache so the new reaction appears without a
		// page refresh — feature parity with inline comments pubsub behaviour.
		// fetchCommentById fetches the updated comment (including its reactionsSummary)
		// and writes it into the shared cache that the comments panel reads from.
		const numericCommentId = extractNumericCommentId(event.commentId);
		if (addCommentInCache && fetchCommentById && contentId) {
			void fetchCommentById({
				commentId: numericCommentId,
				contentId,
				fetchPolicy: 'network-only',
			}).then((comment: any) => {
				if (comment) {
					addCommentInCache({ newComment: comment });
					if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
						const updatedData = getCommentsDataFromCache?.(contentId);
						getProcessedCommentsDataActions?.().processCommentsData({
							data: updatedData,
							pageMode: 'view',
						});
					} else {
						triggerCommentPanelRefresh?.();
					}
				}
			});
		}

		// Mark the reaction comment as unread to trigger the blue-dot indicator
		// in the content wrapper — matching inline comment pubsub behaviour.
		if (updateUnreadCommentIDSet) {
			updateUnreadCommentIDSet(new Set([numericCommentId]), 'add');
		}

		showReactionFlag({
			commentId: event.commentId,
			markerRef: event.markerRef,
			emojiId: event.emojiId,
			contentType: contentType ?? 'page',
			actorAccountId: payload?.accountId,
		});
	};

/** See `createInlineReactionAddedHandler` for the factory rationale. */
export const createInlineReactionRemovedHandler =
	(params: InlineReactionsPubSubParams) =>
	(_event: string, payload: ConfluenceOnContentModified): void => {
		if (isSelfAuthored(payload, params.currentUserId)) {
			return;
		}
		const event = payload?.inlineReactionRemoved;
		if (!event || !event.commentId) {
			return;
		}

		// Apply the delta directly to the store for an instant count update.
		// Only when emojiId is present (same guard as the added handler).
		if (event.emojiId) {
			params.applyRemoteDelta(extractNumericCommentId(event.commentId), event.emojiId, -1);
		}

		// No flag for removals — matches inline-comment pubsub behaviour,
		// which only surfaces flags for new comments, not deletions.
		params.refetchReactionsSummary(event.commentId);
	};

/** See `createInlineReactionAddedHandler` for the factory rationale. */
export const createInlineReactionHighlightDeletedHandler =
	(params: InlineReactionsPubSubParams) =>
	(_event: string, payload: ConfluenceOnContentModified): void => {
		if (isSelfAuthored(payload, params.currentUserId)) {
			return;
		}
		const event = payload?.inlineReactionHighlightDeleted;
		if (!event || !event.markerRef) {
			return;
		}

		const {
			rendererActions,
			updateDocument,
			pruneOptimisticReactions,
			setActiveHighlight,
			refetchActiveInlineData,
		} = params;

		const deleteResult = rendererActions?.deleteAnnotation(
			event.markerRef,
			AnnotationTypes.INLINE_COMMENT,
		);
		if (deleteResult && updateDocument) {
			updateDocument(deleteResult.doc as JSONDocNode);
		}

		// Drop the optimistic entry (if any) so the renderer doesn't try to
		// keep painting the highlight after the mark is removed.
		pruneOptimisticReactions([event.markerRef]);
		setActiveHighlight?.(null);

		// Refresh the active-inline-data cache so the markerRef set is
		// correct on subsequent renders.
		refetchActiveInlineData();
	};

export const setUpInlineReactionsPubSub = (
	params: InlineReactionsPubSubParams,
): Array<{ subscriberId: string; delta: string }> => {
	const { addSubscriber, setContentId, setContentType, contentId, contentType } = params;
	if (!addSubscriber || !setContentId || !setContentType) {
		return [];
	}

	setContentType(contentType ?? 'page');
	setContentId(contentId);

	const subscribers = [
		{
			subscriberId: 'inlineReactionsPubSubInlineReactionAdded',
			onSubscriptionData: createInlineReactionAddedHandler(params),
			delta: InlineReactionSubscriptionDeltas.InlineReactionAdded,
		},
		{
			subscriberId: 'inlineReactionsPubSubInlineReactionRemoved',
			onSubscriptionData: createInlineReactionRemovedHandler(params),
			delta: InlineReactionSubscriptionDeltas.InlineReactionRemoved,
		},
		{
			subscriberId: 'inlineReactionsPubSubInlineReactionHighlightDeleted',
			onSubscriptionData: createInlineReactionHighlightDeletedHandler(params),
			delta: InlineReactionSubscriptionDeltas.InlineReactionHighlightDeleted,
		},
	];

	return subscribers.map((subscriber) => ({
		subscriberId: addSubscriber(subscriber),
		delta: subscriber.delta,
	}));
};

export const removeInlineReactionsPubSub = ({
	subscriptions,
	removeSubscriber,
}: InlineReactionsPubSubUnsubParams): void => {
	if (!removeSubscriber) {
		return;
	}
	for (const subscription of subscriptions) {
		removeSubscriber(subscription);
	}
};
